import marketplaceCategories from '@mooveguru/yhh-shared-config/marketplace-categories.json';
import { parsePostalCodeList } from '../../../utils/postalCodes';

export const STATIC_HOME_PRO_CATEGORY_KEYS = [
	'chimney_services_and_repairs',
	'electricians',
	'garage_doors',
	'handymen',
	'hvac',
	'landscapers',
	'plumbers',
	'roofers',
	'sewer_and_draining_cleaning_and_repairs',
	'well_service_and_installation',
];

export const preferredVendorRequestCache = new Map();
export const preferredVendorResponseCache = new Map();

/**
 * @param {string} propertyId
 * @param {string} category
 * @returns {string}
 */
export function getPreferredVendorCacheKey(propertyId, category = '') {
	return `${propertyId}::${category}`;
}

/**
 * @param {string} propertyId
 * @returns {void}
 */
export function clearPreferredVendorCache(propertyId) {
	Array.from(preferredVendorRequestCache.keys()).forEach((key) => {
		if (key.startsWith(`${propertyId}::`)) {
			preferredVendorRequestCache.delete(key);
		}
	});
	Array.from(preferredVendorResponseCache.keys()).forEach((key) => {
		if (key.startsWith(`${propertyId}::`)) {
			preferredVendorResponseCache.delete(key);
		}
	});
}

/**
 * @param {string} categoryKey
 * @returns {string}
 */
export function getCategoryLabel(categoryKey) {
	/** @type {Record<string, string>} */
	const categoryLabels = marketplaceCategories;

	return categoryLabels[categoryKey] ?? categoryKey;
}

/**
 * @returns {string[]}
 */
export function getStaticHomeProCategories() {
	return [...STATIC_HOME_PRO_CATEGORY_KEYS].sort((left, right) =>
		getCategoryLabel(left).localeCompare(getCategoryLabel(right))
	);
}

/**
 * @param {Readonly<{ localOfferList: any[], offerList: any[], tenantOfferList: any[] }>} state
 * @param {{ postalCode?: string }} address
 * @returns {Readonly<{ groupOffers: any[], hasAnyOffers: boolean, localOffers: any[], recommendedOffers: any[], tenantOffers: any[] }>}
 */
export function getCategorySections(state, address) {
	const propertyPostalCodes = new Set([address?.postalCode].filter(Boolean));
	const tenantOffers = state.tenantOfferList.filter(
		(vendor) => !!vendor && vendor.related?.type === 'tenant'
	);
	const localOffers = state.localOfferList.filter((vendor) => {
		if (!vendor || vendor.related?.type !== 'local') {
			return false;
		}

		if (propertyPostalCodes.size < 1) {
			return false;
		}

		const vendorPostalCodes = parsePostalCodeList(vendor.postalCode);

		return vendorPostalCodes.some((postalCode) =>
			propertyPostalCodes.has(postalCode)
		);
	});
	const marketplaceOffers = state.offerList.filter(
		(vendor) =>
			!!vendor &&
			vendor.related?.type !== 'tenant' &&
			vendor.related?.type !== 'local'
	);
	const recommendedOffers = marketplaceOffers.filter(
		(vendor) => vendor.related?.type === 'agent'
	);
	const groupOffers = marketplaceOffers.filter(
		(vendor) => vendor.related?.type === 'office'
	);

	return {
		groupOffers,
		hasAnyOffers:
			tenantOffers.length > 0 ||
			localOffers.length > 0 ||
			recommendedOffers.length > 0 ||
			groupOffers.length > 0 ||
			marketplaceOffers.filter(
				(vendor) =>
					vendor.related?.type !== 'agent' &&
					vendor.related?.type !== 'office'
			).length > 0,
		localOffers,
		recommendedOffers,
		tenantOffers,
	};
}

/**
 * @param {Record<string, { isLoading: boolean, localOfferList: any[], offerList: any[], tenantOfferList: any[] }>} categoryResults
 * @param {string[]} selectedCategories
 * @returns {{ isLoading: boolean, localOfferList: any[], offerList: any[], tenantOfferList: any[] }}
 */
export function getCombinedCategoryState(categoryResults, selectedCategories) {
	const selectedStates = selectedCategories
		.map((category) => categoryResults[category])
		.filter(Boolean);

	return {
		isLoading: selectedStates.some((state) => state.isLoading),
		localOfferList: Array.from(
			new Map(
				selectedStates
					.flatMap((state) => state.localOfferList)
					.map((vendor) => [vendor.id, vendor])
			).values()
		),
		offerList: Array.from(
			new Map(
				selectedStates
					.flatMap((state) => state.offerList)
					.map((vendor) => [vendor.id, vendor])
			).values()
		),
		tenantOfferList: Array.from(
			new Map(
				selectedStates
					.flatMap((state) => state.tenantOfferList)
					.map((vendor) => [vendor.id, vendor])
			).values()
		),
	};
}

/**
 * @param {any[]} vendors
 * @returns {any[][]}
 */
export function createMarketplaceVendorRows(vendors) {
	const rows = [];
	let index = 0;

	while (index < vendors.length) {
		const remaining = vendors.length - index;

		if (remaining === 4) {
			rows.push(vendors.slice(index, index + 2));
			rows.push(vendors.slice(index + 2, index + 4));
			break;
		}

		if (remaining === 5) {
			rows.push(vendors.slice(index, index + 2));
			rows.push(vendors.slice(index + 2, index + 5));
			break;
		}

		const rowSize = Math.min(3, remaining);
		rows.push(vendors.slice(index, index + rowSize));
		index += rowSize;
	}

	return rows;
}

/**
 * @param {string[]} preferredVendorCategories
 * @param {string[]} selectedCategories
 * @returns {string[]}
 */
export function getVendorFilterCategories(
	preferredVendorCategories,
	selectedCategories
) {
	const categoryMap = new Map();

	selectedCategories.forEach((category) => {
		categoryMap.set(category, getCategoryLabel(category));
	});
	preferredVendorCategories.forEach((category) => {
		if (!categoryMap.has(category)) {
			categoryMap.set(category, getCategoryLabel(category));
		}
	});

	return Array.from(categoryMap.entries())
		.sort((left, right) => left[1].localeCompare(right[1]))
		.map(([category]) => category);
}
